import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Shield,
    AlertTriangle,
    Eye,
    Lock,
    Users,
    Clock,
    TrendingUp,
    Activity,
    AlertCircle,
    CheckCircle,
    XCircle,
    Info
} from 'lucide-react';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

interface SecurityEvent {
    id: string;
    event_type: string;
    user_id?: string;
    ip_address: string;
    endpoint?: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    details: string;
    created_at: string;
    is_resolved: boolean;
}

interface RateLimitStats {
    total_requests: number;
    blocked_requests: number;
    unique_ips: number;
    top_endpoints: Array<{
        endpoint: string;
        request_count: number;
    }>;
}

interface AuditLogStats {
    total_events: number;
    successful_events: number;
    failed_events: number;
    recent_events: Array<{
        event_type: string;
        user_id?: string;
        success: boolean;
        created_at: string;
    }>;
}

const SecurityDashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const { formatDateTime, formatRelativeTime } = useDateTimeFormat();
    const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
    const [rateLimitStats, setRateLimitStats] = useState<RateLimitStats | null>(null);
    const [auditLogStats, setAuditLogStats] = useState<AuditLogStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchSecurityData();
    }, []);

    const fetchSecurityData = async () => {
        try {
            setLoading(true);
            // In a real implementation, these would be API calls to your n8n workflows
            // For now, we'll simulate the data
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Simulated data - replace with actual API calls
            setSecurityEvents([
                {
                    id: '1',
                    event_type: 'failed_login',
                    user_id: 'user123',
                    ip_address: '192.168.1.100',
                    endpoint: '/auth/login',
                    severity: 'medium',
                    details: 'Multiple failed login attempts detected',
                    created_at: new Date(Date.now() - 300000).toISOString(),
                    is_resolved: false
                },
                {
                    id: '2',
                    event_type: 'rate_limit_exceeded',
                    ip_address: '10.0.0.50',
                    endpoint: '/api/users',
                    severity: 'low',
                    details: 'Rate limit exceeded for API endpoint',
                    created_at: new Date(Date.now() - 600000).toISOString(),
                    is_resolved: true
                }
            ]);

            setRateLimitStats({
                total_requests: 15420,
                blocked_requests: 234,
                unique_ips: 89,
                top_endpoints: [
                    { endpoint: '/api/users', request_count: 3420 },
                    { endpoint: '/api/auth', request_count: 2890 },
                    { endpoint: '/api/profile', request_count: 2100 }
                ]
            });

            setAuditLogStats({
                total_events: 45678,
                successful_events: 45234,
                failed_events: 444,
                recent_events: [
                    {
                        event_type: 'token_introspection',
                        user_id: 'user123',
                        success: true,
                        created_at: new Date(Date.now() - 120000).toISOString()
                    },
                    {
                        event_type: 'user_login',
                        user_id: 'user456',
                        success: true,
                        created_at: new Date(Date.now() - 180000).toISOString()
                    }
                ]
            });

        } catch (err) {
            setError('Failed to load security data');
            console.error('Error fetching security data:', err);
        } finally {
            setLoading(false);
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'destructive';
            case 'high': return 'destructive';
            case 'medium': return 'default';
            case 'low': return 'secondary';
            default: return 'secondary';
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'critical': return <XCircle className="h-4 w-4" />;
            case 'high': return <AlertTriangle className="h-4 w-4" />;
            case 'medium': return <AlertCircle className="h-4 w-4" />;
            case 'low': return <Info className="h-4 w-4" />;
            default: return <Info className="h-4 w-4" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading security dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{t('security.dashboard.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('security.dashboard.description')}
                    </p>
                </div>
                <Button onClick={fetchSecurityData} variant="outline">
                    <Activity className="h-4 w-4 mr-2" />
                    {t('common.refresh')}
                </Button>
            </div>

            {/* Security Overview Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('security.dashboard.active_threats')}
                        </CardTitle>
                        <Shield className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {securityEvents.filter(e => !e.is_resolved).length}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('security.dashboard.unresolved_events')}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('security.dashboard.blocked_requests')}
                        </CardTitle>
                        <Lock className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {rateLimitStats?.blocked_requests || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('security.dashboard.last_24h')}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('security.dashboard.total_requests')}
                        </CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {rateLimitStats?.total_requests.toLocaleString() || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('security.dashboard.last_24h')}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            {t('security.dashboard.unique_ips')}
                        </CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {rateLimitStats?.unique_ips || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('security.dashboard.active_connections')}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Detailed Security Information */}
            <Tabs defaultValue="events" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="events">{t('security.dashboard.security_events')}</TabsTrigger>
                    <TabsTrigger value="rate-limits">{t('security.dashboard.rate_limits')}</TabsTrigger>
                    <TabsTrigger value="audit-logs">{t('security.dashboard.audit_logs')}</TabsTrigger>
                </TabsList>

                <TabsContent value="events" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('security.dashboard.recent_security_events')}</CardTitle>
                            <CardDescription>
                                {t('security.dashboard.security_events_description')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {securityEvents.map((event) => (
                                    <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                                        <div className="flex items-center space-x-4">
                                            {getSeverityIcon(event.severity)}
                                            <div>
                                                <div className="font-medium">
                                                    {t(`security.events.${event.event_type}`)}
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {event.ip_address} • {event.endpoint}
                                                    {event.user_id && ` • ${event.user_id}`}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {formatRelativeTime(event.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Badge variant={getSeverityColor(event.severity)}>
                                                {event.severity}
                                            </Badge>
                                            <Badge variant={event.is_resolved ? 'default' : 'secondary'}>
                                                {event.is_resolved ? t('common.resolved') : t('common.pending')}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="rate-limits" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('security.dashboard.rate_limit_statistics')}</CardTitle>
                            <CardDescription>
                                {t('security.dashboard.rate_limit_description')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <h4 className="font-medium">{t('security.dashboard.top_endpoints')}</h4>
                                        {rateLimitStats?.top_endpoints.map((endpoint, index) => (
                                            <div key={index} className="flex justify-between items-center p-2 border rounded">
                                                <span className="font-mono text-sm">{endpoint.endpoint}</span>
                                                <Badge variant="outline">{endpoint.request_count.toLocaleString()}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-2">
                                        <h4 className="font-medium">{t('security.dashboard.block_rate')}</h4>
                                        <div className="text-2xl font-bold">
                                            {rateLimitStats ?
                                                ((rateLimitStats.blocked_requests / rateLimitStats.total_requests) * 100).toFixed(2)
                                                : 0}%
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('security.dashboard.requests_blocked')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="audit-logs" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('security.dashboard.audit_log_summary')}</CardTitle>
                            <CardDescription>
                                {t('security.dashboard.audit_log_description')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-3">
                                    <div className="text-center p-4 border rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">
                                            {auditLogStats?.successful_events.toLocaleString() || 0}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('security.dashboard.successful_events')}
                                        </p>
                                    </div>
                                    <div className="text-center p-4 border rounded-lg">
                                        <div className="text-2xl font-bold text-red-600">
                                            {auditLogStats?.failed_events.toLocaleString() || 0}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('security.dashboard.failed_events')}
                                        </p>
                                    </div>
                                    <div className="text-center p-4 border rounded-lg">
                                        <div className="text-2xl font-bold">
                                            {auditLogStats?.total_events.toLocaleString() || 0}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('security.dashboard.total_events')}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium mb-2">{t('security.dashboard.recent_audit_events')}</h4>
                                    <div className="space-y-2">
                                        {auditLogStats?.recent_events.map((event, index) => (
                                            <div key={index} className="flex items-center justify-between p-2 border rounded">
                                                <div className="flex items-center space-x-2">
                                                    {event.success ?
                                                        <CheckCircle className="h-4 w-4 text-green-600" /> :
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    }
                                                    <span className="font-medium">
                                                        {t(`security.events.${event.event_type}`)}
                                                    </span>
                                                    {event.user_id && (
                                                        <span className="text-sm text-muted-foreground">
                                                            • {event.user_id}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-sm text-muted-foreground">
                                                    {formatRelativeTime(event.created_at)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default SecurityDashboardPage;



